'use client';

/**
 * Dock — the floating presenter bar (MOTION-SPEC §9).
 *
 * Fixed at the bottom centre (z 1005, under every dialog at 1020), glass
 * surface, auto-hides. Left → right: prev, ten scene dots (current one an
 * elongated lime pill with its mono index; hover label chips), next, persona
 * segmented control with a sliding highlight, tour play/pause with a conic
 * progress ring, Ask AI, scene map, share, deconstruct, fullscreen, help.
 *
 * Presence: shown for 3000 ms after load and after each scene change, on any
 * pointer move, while hovered / focus-within / popover open / tour paused,
 * and while the shell says `visible`; hidden 2400 ms after the last move. On
 * coarse pointers it is pinned and compact; ≤ 720 px the shell's mobile bar
 * takes over and this bar is not displayed.
 *
 * Reads everything from useLiving(). Events dispatched on `window`:
 * `sn:deconstruct` (deconstruct button) and `sn:help` (help button). The
 * share button only calls `share()`; the toast belongs to the shell.
 *
 * The persona radiogroup owns ArrowLeft/Right/Up/Down/Home/End with a roving
 * tabindex. Because the shell's keyboard map is a capture-phase listener on
 * `window`, the dock registers its own capture listener (child effects run
 * first, so it precedes the shell's) and stops immediate propagation for
 * those keys while focus is inside the radiogroup.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { REFERENCE_SCENES, REFERENCE_SCENE_LABELS, type ReferenceSceneId } from '../../reference/types';
import { useLiving } from '../context';
import { PERSONA_META } from '../personas';
import { PERSONAS, type Persona } from '../types';
import { DOCK_COPY, lensAria, sceneAria } from './copy';
import { Icon, type IconName } from './icons';
import { useDockPresence } from './usePresence';
import { useMagnet } from './useMagnet';
import { useMediaQuery } from './useMediaQuery';
import styles from './Dock.module.css';

export interface DockProps {
  sceneId: ReferenceSceneId;
  /** Shell tells the dock when the pointer moved recently; the dock owns its own idle timer otherwise. */
  visible: boolean;
}

const RADIO_KEYS = new Set(['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End']);

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function Tip({ text, keys }: { text: string; keys?: string }) {
  return (
    <span className={styles.tip} aria-hidden="true">
      <span>{text}</span>
      {keys ? <kbd>{keys}</kbd> : null}
    </span>
  );
}

interface ActionProps {
  testId: string;
  label: string;
  /** Tooltip text; defaults to the label. */
  tip?: string;
  /** Visible key hint inside the tooltip. */
  keys: string;
  /** aria-keyshortcuts value. */
  shortcut: string;
  icon: IconName;
  onClick: () => void;
  className?: string;
  nav?: boolean;
  style?: CSSProperties;
  data?: Record<string, string | undefined>;
  children?: ReactNode;
}

function Action({ testId, label, tip, keys, shortcut, icon, onClick, className, nav, style, data, children }: ActionProps) {
  return (
    <button
      type="button"
      className={`${styles.btn} ${nav ? styles.nav : ''} ${className ?? ''}`}
      data-testid={testId}
      data-magnet=""
      aria-label={label}
      aria-keyshortcuts={shortcut}
      onClick={onClick}
      style={style}
      {...data}
    >
      {children}
      <Icon name={icon} />
      <Tip text={tip ?? label} keys={keys} />
    </button>
  );
}

export default function Dock({ sceneId, visible }: DockProps) {
  const {
    lang,
    persona,
    setPersona,
    tour,
    navigate,
    openAssistant,
    toggleFullscreen,
    openMap,
    share,
    reducedMotion,
    touch,
  } = useLiving();

  const navRef = useRef<HTMLElement>(null);
  const lensWrapRef = useRef<HTMLDivElement>(null);
  const lensButtonRef = useRef<HTMLButtonElement>(null);
  const radioRefs = useRef<Partial<Record<Persona, HTMLButtonElement | null>>>({});
  const personaRef = useRef(persona);
  const popId = useId();

  const [popOpen, setPopOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState<{ enabled: boolean; active: boolean }>({ enabled: true, active: false });
  const compactPersona = useMediaQuery('(max-width: 1099px)');

  const total = REFERENCE_SCENES.length;
  const index = Math.max(0, REFERENCE_SCENES.indexOf(sceneId));
  const personaIndex = Math.max(0, PERSONAS.indexOf(persona));
  const personaMeta = PERSONA_META[personaIndex] ?? PERSONA_META[0];
  const tourPaused = !tour.playing && tour.progress > 0;
  const tourFinished = !tour.playing && index === total - 1 && tour.progress >= 0.999;

  const { state, wake } = useDockPresence({
    visible,
    pinned: touch,
    hold: popOpen || tourPaused,
    sceneIndex: index,
    touring: tour.playing,
  });
  useMagnet(navRef, !touch && !reducedMotion);

  useEffect(() => {
    personaRef.current = persona;
  }, [persona]);

  /* Fullscreen availability and state. */
  useEffect(() => {
    const sync = () => setFullscreen({ enabled: Boolean(document.fullscreenEnabled), active: Boolean(document.fullscreenElement) });
    sync();
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  /* Persona radios: roving tabindex + arrows, owned here (see header comment). */
  const moveRadio = useCallback((key: string) => {
    const current = PERSONAS.indexOf(personaRef.current);
    let next = current;
    if (key === 'ArrowRight' || key === 'ArrowDown') next = (current + 1) % PERSONAS.length;
    else if (key === 'ArrowLeft' || key === 'ArrowUp') next = (current + PERSONAS.length - 1) % PERSONAS.length;
    else if (key === 'Home') next = 0;
    else if (key === 'End') next = PERSONAS.length - 1;
    const id = PERSONAS[next];
    setPersona(id);
    radioRefs.current[id]?.focus();
  }, [setPersona]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!RADIO_KEYS.has(event.key) || event.isComposing || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : null;
      const root = navRef.current;
      if (!target || !root || !root.contains(target) || !target.closest('[data-sn-radios]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      moveRadio(event.key);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [moveRadio]);

  /* Collapsed persona popover: closes on outside pointerdown, Esc, focus leaving, or growing back to the segment. */
  useEffect(() => {
    if (!compactPersona && popOpen) setPopOpen(false);
  }, [compactPersona, popOpen]);

  useEffect(() => {
    if (!popOpen) return;
    const onDown = (event: PointerEvent) => {
      const wrap = lensWrapRef.current;
      if (wrap && event.target instanceof Node && !wrap.contains(event.target)) setPopOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      event.preventDefault();
      setPopOpen(false);
      lensButtonRef.current?.focus();
    };
    document.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    const frame = requestAnimationFrame(() => radioRefs.current[personaRef.current]?.focus());
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
      cancelAnimationFrame(frame);
    };
  }, [popOpen]);

  const choosePersona = useCallback((id: Persona) => {
    setPersona(id);
    if (popOpen) {
      setPopOpen(false);
      lensButtonRef.current?.focus();
    }
  }, [popOpen, setPersona]);

  /* Actions. */
  const onPlay = useCallback(() => {
    if (tourFinished) {
      navigate(REFERENCE_SCENES[0]);
      tour.play();
      return;
    }
    tour.toggle();
  }, [navigate, tour, tourFinished]);

  const onShare = useCallback(() => {
    void share();
  }, [share]);

  const onDeconstruct = useCallback(() => {
    window.dispatchEvent(new CustomEvent('sn:deconstruct'));
  }, []);

  const onHelp = useCallback(() => {
    window.dispatchEvent(new CustomEvent('sn:help'));
  }, []);

  const t = DOCK_COPY;
  const spaceKey = t.space[lang];
  const playLabel = tourFinished ? t.replay[lang] : tour.playing ? t.pause[lang] : tourPaused ? t.resume[lang] : t.play[lang];
  const playTip = tourFinished ? t.replay[lang] : tour.playing ? t.pauseShort[lang] : tourPaused ? t.resume[lang] : t.play[lang];
  const playIcon: IconName = tourFinished ? 'replay' : tour.playing ? 'pause' : 'play';

  const radios = (vertical: boolean) => PERSONA_META.map((meta) => {
    const checked = meta.id === persona;
    return (
      <button
        key={meta.id}
        ref={(node) => { radioRefs.current[meta.id] = node; }}
        type="button"
        role="radio"
        aria-checked={checked}
        tabIndex={checked ? 0 : -1}
        className={vertical ? styles.popItem : styles.chip}
        data-testid={`sn-dock-persona-${meta.id}`}
        aria-label={lensAria(lang, meta.label[lang])}
        onClick={() => choosePersona(meta.id)}
      >
        {meta.label[lang]}
        {vertical ? <small>{meta.focus[lang]}</small> : <Tip text={meta.focus[lang]} keys="P" />}
      </button>
    );
  });

  return (
    <>
      <nav
        ref={navRef}
        id="sn-dock"
        className={styles.dock}
        data-testid="sn-dock"
        role="group"
        aria-label={t.group[lang]}
        data-state={state}
        data-input={touch ? 'coarse' : 'fine'}
        data-reduced={reducedMotion ? 'true' : undefined}
        tabIndex={-1}
        onFocus={wake}
      >
        <Action testId="sn-dock-prev" nav label={t.prev[lang]} keys="←" shortcut="ArrowLeft" icon="prev" onClick={() => navigate(-1)} />

        <div className={styles.dots} role="group" aria-label={t.scenes[lang]}>
          {REFERENCE_SCENES.map((id, i) => {
            const n = i + 1;
            const current = i === index;
            const side = i < index ? -1 : i > index ? 1 : 0;
            const label = REFERENCE_SCENE_LABELS[id][lang];
            const digit = String(n % 10);
            return (
              <button
                key={id}
                type="button"
                className={styles.dot}
                data-testid={`sn-dock-scene-${n}`}
                aria-label={sceneAria(lang, n, total, label)}
                aria-current={current ? 'step' : undefined}
                aria-keyshortcuts={digit}
                style={{ '--side': side } as CSSProperties}
                onClick={() => navigate(id)}
              >
                <i className={styles.pip} aria-hidden="true"><span className={styles.idx}>{pad(n)}</span></i>
                <Tip text={label} keys={digit} />
              </button>
            );
          })}
        </div>
        <span className={styles.counter} aria-hidden="true"><b>{pad(index + 1)}</b> / {total}</span>

        <Action testId="sn-dock-next" nav label={t.next[lang]} keys="→" shortcut="ArrowRight" icon="next" onClick={() => navigate(1)} />

        <i className={styles.sep} aria-hidden="true" />

        {compactPersona ? (
          <div ref={lensWrapRef} className={styles.lensWrap}>
            <button
              ref={lensButtonRef}
              type="button"
              className={`${styles.btn} ${styles.lensBtn}`}
              data-magnet=""
              aria-label={`${t.lensPick[lang]}: ${personaMeta.label[lang]}`}
              aria-keyshortcuts="P"
              aria-haspopup="true"
              aria-expanded={popOpen}
              aria-controls={popOpen ? popId : undefined}
              onClick={() => setPopOpen((open) => !open)}
            >
              <Icon name="lens" />
              <b>{personaMeta.label[lang]}</b>
              <Tip text={t.lensPick[lang]} keys="P" />
            </button>
            {popOpen && (
              <div id={popId} className={styles.pop} role="radiogroup" aria-label={t.lens[lang]} data-sn-radios="">
                {radios(true)}
              </div>
            )}
          </div>
        ) : (
          <div
            className={styles.seg}
            role="radiogroup"
            aria-label={t.lens[lang]}
            aria-keyshortcuts="P"
            data-sn-radios=""
            style={{ '--sn-seg-i': personaIndex } as CSSProperties}
          >
            <i className={styles.thumb} aria-hidden="true" />
            {radios(false)}
          </div>
        )}

        <i className={styles.sep} aria-hidden="true" />

        <Action
          testId="sn-dock-play"
          className={styles.play}
          label={playLabel}
          tip={playTip}
          keys={spaceKey}
          shortcut="Space"
          icon={playIcon}
          onClick={onPlay}
          style={{ '--sn-dock-p-fallback': tour.progress.toFixed(4) } as CSSProperties}
          data={{ 'data-playing': tour.playing ? 'true' : 'false', 'data-finished': tourFinished ? 'true' : undefined }}
        >
          <i className={styles.ring} aria-hidden="true" />
        </Action>
        <Action testId="sn-dock-ask" className={styles.ask} label={t.ask[lang]} keys="Q" shortcut="Q" icon="ask" onClick={() => openAssistant()} />
        <Action testId="sn-dock-map" label={t.map[lang]} keys="M" shortcut="M" icon="map" onClick={openMap} />
        <Action testId="sn-dock-share" label={t.share[lang]} keys="S" shortcut="S" icon="share" onClick={onShare} />
        <Action testId="sn-dock-deconstruct" label={t.deconstruct[lang]} keys="D" shortcut="D" icon="deconstruct" onClick={onDeconstruct} />
        {fullscreen.enabled && (
          <Action
            testId="sn-dock-fullscreen"
            label={fullscreen.active ? t.exitFullscreen[lang] : t.fullscreen[lang]}
            keys="F"
            shortcut="F"
            icon={fullscreen.active ? 'exitFullscreen' : 'fullscreen'}
            onClick={toggleFullscreen}
            data={{ 'data-active': fullscreen.active ? 'true' : undefined }}
          />
        )}
        <Action testId="sn-dock-help" label={t.help[lang]} keys="?" shortcut="Shift+?" icon="help" onClick={onHelp} />
      </nav>
      <i className={styles.handle} data-on={state === 'hidden' ? 'true' : 'false'} aria-hidden="true" />
    </>
  );
}
